import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { logoUrl } from '../net/apiClient.js'
import { Marke } from '../screens/Marke.js'
import { Zeichen, type ZeichenName } from './Zeichen.js'
import { Ausstiegsband } from './Ausstiegsband.js'
import { Befehlszeile, useBefehlstaste, type Befehl } from './Befehlszeile.js'
import { beobachteTabellen } from './Tabellenbeschriftung.js'
import { Toasts } from './Toasts.js'

/**
 * Der Rahmen der Büroansicht.
 *
 * Seitenleiste links, Inhalt rechts, Reihenfolge und Gruppierung wie in der
 * Vorlage. Der Aufbau ist übernommen, weil der Betrieb ihn kennt – wer seit
 * Jahren "Express-Rechnung" an zweiter Stelle sucht, soll sie dort finden.
 *
 * Zwei Einträge gibt es zusätzlich: Rapporte und Projekte. Sie stehen bewusst
 * unter dem Belegblock – sichtbar, aber nicht im Weg des Alltags.
 *
 * Diesen Rahmen sieht nur der Superuser. Der Monteur behält die bestehende
 * Oberfläche; sie ist für die Baustelle gebaut und hätte von einer
 * Seitenleiste nichts.
 */

export interface OfficeEintrag {
  id: string
  label: string
  icon: ZeichenName
  /**
   * Nur für Betriebe mit Betreuungsverhältnissen – heute die Kita.
   *
   * Ein Eintrag, den fünfzehn von sechzehn Branchen nie anfassen, ist in
   * deren Liste eine Zeile zum Überlesen. Die Zusicherung liegt trotzdem am
   * Server; siehe die Begründung am Eintrag selbst.
   */
  nurBetreuung?: boolean
  /**
   * Nur für Betriebe, die zu einer Kita-Gemeinschaft gehören.
   *
   * Dieselbe Begründung wie bei `nurBetreuung`: Der Eintrag wäre für alle
   * anderen eine Zeile zum Überlesen. Die Zusicherung liegt am Server – wer
   * die Adresse errät, bekommt 404, weil sein Betrieb keiner Gemeinschaft
   * angehört.
   */
  nurGemeinschaft?: boolean
}

export interface OfficeGruppe {
  /** Fehlt die Überschrift, steht die Gruppe ohne Zwischenzeile da. */
  titel?: string
  eintraege: OfficeEintrag[]
}

/*
  Neun Einträge in drei benannten Gruppen – plus Übersicht oben und
  Einstellungen unten.

  Hier stand vorher: «flach, ohne Gruppenüberschriften», mit der Begründung,
  dass gezeichnete Zeichen jedem Eintrag ein eigenes Bild geben und die
  Überschriften dadurch zu Zwischenzeilen ohne Aufgabe würden.

  Das stimmt für das Wiederfinden und nicht für das erste Mal. Ein Zeichen
  erkennt, wer weiss, wonach er sucht; wer die Software neu vor sich hat,
  liest neun gleichrangige Wörter und muss jedes einzeln prüfen. Die
  Gruppen beantworten vorher, in welchem Drittel er suchen muss:

    Belege     – was hinausgeht und was hereinkommt
    Arbeit     – was auf der Baustelle passiert
    Stammdaten – was sich selten ändert und überall eingesetzt wird

  Das ist keine Dekoration, sondern dieselbe Einteilung, nach der im Büro
  ohnehin gedacht wird. Übersicht steht über allem, weil sie kein Bereich
  ist, sondern der Blick auf alle; Einstellungen steht unten abgesetzt, weil
  man dort hingeht und nicht arbeitet.

  Drei Einträge fehlen weiterhin bewusst, und keine Funktion ist es:

    Sicherheit steht in den Einstellungen, wo sie hingehört – dort ist sie
    ein Abschnitt und keine eigene Welt.

    Express-Rechnung und Express-Offerte sind keine Bereiche, sondern
    Handgriffe. Sie stehen als zwei Knöpfe über der Liste, immer sichtbar,
    und nicht als Reiter, den man «betritt».
*/
export const OFFICE_GRUPPEN: OfficeGruppe[] = [
  {
    /*
      «Was ist offen» stand hier nicht – der Bildschirm war gebaut, wurde
      gerendert und war über kein Menü erreichbar. Hin kam man nur über einen
      Knopf auf der Übersicht, und den gibt es erst ab dreizehn Hinweisen. In
      der Brotkrume hiess er «Übersicht», weil die Kennung in keiner Liste
      stand.
    */
    eintraege: [
      { id: 'dashboard', label: 'Übersicht', icon: 'uebersicht' },
      { id: 'offen', label: 'Was ist offen', icon: 'haken' }
    ]
  },
  {
    titel: 'Belege',
    eintraege: [
      { id: 'rechnungen', label: 'Rechnungen', icon: 'rechnung' },
      { id: 'offerten', label: 'Offerten', icon: 'offerte' },
      { id: 'zahlungen', label: 'Zahlungen', icon: 'zahlung' }
    ]
  },
  {
    titel: 'Arbeit',
    eintraege: [
      { id: 'rapporte', label: 'Rapporte', icon: 'rapport' },
      { id: 'projekte', label: 'Projekte', icon: 'projekt' }
    ]
  },
  {
    titel: 'Stammdaten',
    eintraege: [
      { id: 'kunden', label: 'Kunden', icon: 'kunde' },
      /*
        «Betreuung» sieht nur eine Kita.

        Der Eintrag steht in dieser Gruppe, weil ein Betreuungsverhältnis
        Stammdaten sind: einmal eingerichtet, monatlich benutzt. Er steht
        NICHT bei den Belegen – die Monatsrechnungen entstehen zwar dort, aber
        gesucht wird der Bereich über das Kind und nicht über die Rechnung.

        `nurBetreuung` filtert ihn heraus, wo es keine Kita ist. Das ist eine
        Aufräumung der Liste und keine Berechtigung: Jede Route prüft die
        Branche am Server, und ein Parkettleger, der die Adresse errät,
        bekommt 403.
      */
      { id: 'betreuung', label: 'Betreuung', icon: 'kunde', nurBetreuung: true },
      { id: 'katalog', label: 'Leistungen', icon: 'leistung' }
    ]
  }
]

/** Unten, abgesetzt: man geht dorthin, man arbeitet nicht dort. */
export const OFFICE_UNTEN: OfficeEintrag[] = [
  /*
    «Mein Raporto» stand hier nicht – und deshalb gab es den Bereich für den
    Betrieb nicht.

    Produktionsbefund: Bildschirm, Route und Schnittstelle waren vollständig
    gebaut und liefen, `ABO_SELBSTBEDIENUNG` stand produktiv auf `true`, und
    kein Inhaber kam hin. Der Reiter war in `TABS` in App.tsx eingetragen, und
    diese Liste zeichnet nur die Monteursoberfläche; das Büro zeichnet aus
    dieser Liste hier. Der Eintrag war also genau dort, wo ihn niemand sieht,
    der ihn sehen darf. Eine Probe, die nur den Quelltext von App.tsx las, war
    dabei grün.

    Er steht in dieser Gruppe und nicht bei den Einstellungen: Was der Betrieb
    bei uns gebucht hat, ist keine Einstellung seiner Software. Zwischen
    Zahlungskonditionen und Leistungskatalog fände es niemand.

    Sichtbar ist er nur dem Inhaber, und das ohne eigene Abfrage: Diesen
    Rahmen bekommt ausschliesslich der Superuser, der Monteur behält seine
    eigene Oberfläche. Der Schutz steht ohnehin am Server – jede Abo-Route
    weist eine andere Rolle mit 403 ab.
  */
  { id: 'meinraporto', label: 'Mein Raporto', icon: 'paket' },
  /*
    Team steht hier, weil es sonst niemand findet.

    Die Seite gab es längst – einladen, Rolle ändern, deaktivieren,
    Passwortlink, zweiten Faktor zurücksetzen –, aber nur als Abschnitt tief in
    den Einstellungen, und der Startabschnitt dort heisst «Firma». In der
    Vorführung hiess das: «Mitarbeitende einladen gibt es nicht.» Es gab sie,
    man kam nur nicht hin. Auch die Befehlszeile half nicht, denn sie baut ihre
    Sprungliste aus genau dieser Liste.

    Ein eigener Eintrag ist die kleinste ehrliche Antwort darauf.
  */
  { id: 'team', label: 'Team', icon: 'kunde' },
  /*
    «Kita-Gemeinschaft» – nur wo es eine gibt.

    Hier unten und nicht bei den Stammdaten: Der gemeinsame Name, das Logo
    und die Liste der Inhaberinnen sind nichts, was im Tagesgeschäft
    angefasst wird. Man geht einmal hin, richtet es ein, und dann wieder,
    wenn eine Inhaberin dazukommt.
  */
  { id: 'gemeinschaft', label: 'Kita-Gemeinschaft', icon: 'kunde', nurGemeinschaft: true },
  { id: 'einstellungen', label: 'Einstellungen', icon: 'einstellung' }
]

/**
 * Nur für den Betreiber von Raporto.
 *
 * Steht bewusst NICHT in `OFFICE_GRUPPEN` oder `OFFICE_UNTEN`: die beiden
 * Listen füttern auch die Befehlszeile und die Brotkrume, und ein Bereich, den
 * ein Betrieb nie betreten darf, soll dort nicht einmal als Wort auftauchen.
 *
 * Geschützt ist die Ansicht ohnehin am Server; dieser Eintrag ist die
 * Bequemlichkeit, nicht der Schutz.
 */
export const OFFICE_BETREIBER: OfficeEintrag = {
  id: 'betreiber',
  label: 'Betreiber',
  icon: 'einstellung'
}

/**
 * Alle Einträge in einer Reihe – für alles, was nur den Namen zu einer
 * Kennung braucht und die Gruppierung nicht interessiert (etwa die
 * Brotkrume in der Kopfleiste).
 */
export const OFFICE_NAV: OfficeEintrag[] = [
  ...OFFICE_GRUPPEN.flatMap((g) => g.eintraege),
  ...OFFICE_UNTEN
]

/**
 * Der erste Buchstabe des Betriebs für die Kachel.
 *
 * Nicht firma[0]: «Ärni Bodenbeläge» fängt mit einem Zeichen an, das der
 * Browser gross anders setzt als klein, und «  AV Parkett» mit einem
 * Leerzeichen. Deshalb erst trimmen, dann den ersten echten Buchstaben
 * nehmen – und wenn gar keiner da ist, lieber nichts als ein Kästchen.
 */
function ersterBuchstabe(firma: string): string {
  const treffer = firma.trim().match(/\p{L}|\p{N}/u)
  return treffer ? treffer[0].toLocaleUpperCase('de-CH') : ''
}

/*
  Bildschirme, die in der Seitenleiste nicht vorkommen, aber einen Ort haben.

  Die Express-Rechnung ist kein Bereich – man betritt sie nicht, man macht sie
  und geht wieder. In der Brotkrume braucht sie trotzdem einen Namen: dort
  stand vorher «Übersicht», weil die Kennung in der Bereichsliste nicht zu
  finden war. Eine Brotkrume, die einen falschen Ort nennt, ist schlimmer als
  gar keine – man hält sie für kaputt oder, schlimmer, man glaubt ihr.
*/
const WEITERE_ORTE: Record<string, string> = {
  betreiber: 'Betreiber',
  express: 'Express-Rechnung',
  expressofferte: 'Express-Offerte',
  sicherheit: 'Sicherheit',
  status: 'Status'
}

/** Der Name eines Bildschirms für die Brotkrume. */
function bereichsname(id: string): string {
  return OFFICE_NAV.find((e) => e.id === id)?.label ?? WEITERE_ORTE[id] ?? 'Übersicht'
}

export function OfficeShell({
  aktiv,
  aufWechsel,
  logoKey,
  firma,
  benutzer,
  aufAbmelden,
  aufStartseite,
  aufSuche,
  hinweise = 0,
  betreiber = false,
  betreuung = false,
  gemeinschaft = false,
  befehle = [],
  children
}: {
  aktiv: string
  aufWechsel: (id: string) => void
  logoKey: string | null
  firma: string
  benutzer: string
  aufAbmelden: () => Promise<void> | void
  /** Zurück auf die öffentliche Übersicht. */
  aufStartseite?: () => void
  /** Wird gesetzt, sobald es im aktuellen Bereich etwas zu suchen gibt. */
  aufSuche?: (text: string) => void
  /** Wie viele abgeleitete Hinweise offen sind – der Punkt an der Glocke. */
  hinweise?: number
  /** Führt dieser Betrieb Betreuungsverhältnisse? Steuert nur die Liste. */
  betreuung?: boolean
  /** Gehört dieser Betrieb zu einer Kita-Gemeinschaft? Steuert nur die Liste. */
  gemeinschaft?: boolean
  /** Betreibt dieses Konto Raporto selbst? Dann gibt es den Eintrag «Betreiber». */
  betreiber?: boolean
  /**
   * Was die Befehlszeile ausser dem Springen noch anbieten soll.
   *
   * Der Rahmen kennt die Bereiche, aber nicht die Handgriffe darin – er weiss
   * nicht, wie eine Express-Rechnung entsteht. Wer es weiss, reicht es hier
   * herein. Was niemand hereinreicht, steht auch nicht in der Liste; ein
   * Befehl, der nichts tut, wäre schlimmer als keiner.
   */
  befehle?: Befehl[]
  children: ReactNode
}): JSX.Element {
  const [menuOffen, setMenuOffen] = useState(false)
  const [suchtext, setSuchtext] = useState('')
  const [befehlOffen, setBefehlOffen] = useState(false)

  useBefehlstaste(useCallback(() => setBefehlOffen((v) => !v), []))

  /*
    Zu jedem Bereich ein Befehl – und zwar aus derselben Reihe, aus der auch
    die Seitenleiste gebaut wird. Zwei Listen mit denselben Namen wären zwei
    Listen, die nach der ersten Änderung auseinanderlaufen.
  */
  const alleBefehle = useMemo<Befehl[]>(
    () => [
      ...befehle,
      ...OFFICE_NAV.map((e) => ({
        id: `hin-${e.id}`,
        wort: e.label,
        gruppe: 'Springen',
        icon: e.icon,
        tun: () => aufWechsel(e.id)
      }))
    ],
    [befehle, aufWechsel]
  )

  /*
    Nach einem Wechsel schliesst sich das Menü von selbst. Auf dem Handy
    bliebe es sonst offen über dem Bildschirm stehen, den man gerade wollte.

    Das allein reicht aber nicht: wer den Bereich antippt, in dem er schon
    ist, ändert `aktiv` nicht – und das Menü blieb offen stehen, ohne dass
    etwas passierte. Auf dem Telefon sieht das aus, als hätte die App den
    Tipper verschluckt. Deshalb schliesst zusätzlich jeder Tipper im Menü
    selbst (siehe waehle weiter unten).
  */
  useEffect(() => {
    setMenuOffen(false)
  }, [aktiv])

  /*
    Die Listen beschriften ihre Zellen selbst – für das Telefon.

    Siehe ui/Tabellenbeschriftung.ts: auf einem schmalen Gerät wird aus jeder
    Tabellenzeile ein gestapelter Block, und dafür braucht jede Zelle den
    Namen ihrer Spalte. Der Beobachter hängt an der Seite und nicht an einer
    einzelnen Liste, damit er für alle acht gilt – auch für die, die es noch
    nicht gibt.
  */
  useEffect(() => {
    const seite = document.querySelector('.office-page')
    if (!(seite instanceof HTMLElement)) return
    return beobachteTabellen(seite)
  }, [aktiv])

  /* Ein Tipper im Menü wechselt den Bereich UND schliesst das Menü. */
  const waehle = useCallback(
    (id: string): void => {
      setMenuOffen(false)
      aufWechsel(id)
    },
    [aufWechsel]
  )

  return (
    <div className={menuOffen ? 'office menu-offen' : 'office'}>
      {/*
        Die Fläche hinter dem offenen Menü.

        Auf dem Telefon legt sich die Seitenleiste über die Seite, ist aber
        nur 260 Pixel breit – rechts daneben blieb die Liste sichtbar UND
        antippbar. Im Bild einer Messung war das Menü offen und dahinter
        stand ungedimmt die Belegliste; wer danebentippte, landete
        irgendwo, statt das Menü zu schliessen.

        Die Fläche ist ein Knopf und keine Zeichnung: antippen schliesst,
        wie man es von jedem Telefon kennt. Am Bildschirm ist sie
        ausgeblendet – dort steht die Leiste ohnehin fest neben dem Inhalt.
      */}
      {menuOffen && (
        <button
          type="button"
          className="office-menu-grund"
          id="btn-office-menu-zu"
          aria-label="Menü schliessen"
          onClick={() => setMenuOffen(false)}
        />
      )}
      <button
        className="office-menu-knopf"
        id="btn-office-menu"
        type="button"
        onClick={() => setMenuOffen((v) => !v)}
        aria-label="Menü"
      >
        ☰
      </button>

      <aside className="office-side">
        {/*
          Oben steht der Betrieb, nicht wir.

          Das war eine bewusste Umkehr. Vorher stand Raporto zuoberst und das
          Logo des Betriebs darunter – und beim ersten Mandanten, AV Parkett,
          hatten beide denselben Goldton. Man sah nicht zwei Marken, man sah
          eine mit zwei Teilen.

          Wer morgens die Software öffnet, soll seine eigene Firma sehen. Es
          ist sein Betrieb; wir sind die Leitung dahinter. Unsere Marke steht
          unten im Fuss, klein.

          Das Logo wird ungeschönt gezeigt – auch wenn es einen eigenen
          Hintergrund mitbringt und wie aufgeklebt wirkt. Das ist Absicht:
          dasselbe Bild steht später auf jeder Offerte und jeder Rechnung, und
          wer hier sieht, dass es nicht passt, merkt es vor dem ersten Versand
          und nicht danach.
        */}
        <div className="office-side-head">
          {logoKey ? (
            <img className="office-logo" src={logoUrl(logoKey)} alt={firma} />
          ) : (
            /*
              Kein Logo hochgeladen? Dann eine Kachel mit dem Anfangsbuchstaben
              und daneben der Name.

              Hier stand bis eben nur der Name als nackter Text. Ohne diesen
              Zweig begänne die Seitenleiste bei einem frisch eingerichteten
              Betrieb mit «Übersicht» und hätte gar keinen Kopf – und frisch
              eingerichtet sind am Anfang alle. Nackter Text ist aber die
              schlechtere von zwei Antworten: er sieht aus wie ein vergessenes
              Feld. Eine Kachel sieht aus wie ein Platz, an dem etwas steht –
              und genau dorthin kommt später das eigene Logo.
            */
            <span className="office-betrieb">
              <span className="office-betriebszeichen" aria-hidden="true">
                {ersterBuchstabe(firma)}
              </span>
              <span className="office-betriebsname">{firma}</span>
            </span>
          )}
        </div>

        <nav className="office-nav" aria-label="Bereiche">
          {OFFICE_GRUPPEN.map((gruppe, i) => (
            <div className="office-navgruppe" key={gruppe.titel ?? `gruppe-${i}`}>
              {gruppe.titel && <span className="office-navtitel">{gruppe.titel}</span>}
              {gruppe.eintraege
                .filter((e) => !e.nurBetreuung || betreuung)
                .map((e) => (
                <button
                  key={e.id}
                  id={`nav-${e.id}`}
                  type="button"
                  className={e.id === aktiv ? 'active' : undefined}
                  aria-current={e.id === aktiv ? 'page' : undefined}
                  onClick={() => waehle(e.id)}
                >
                  <Zeichen name={e.icon} groesse={18} />
                  <span>{e.label}</span>
                </button>
              ))}
            </div>
          ))}

          <div className="office-navgruppe office-navgruppe-unten">
            {(betreiber ? [OFFICE_BETREIBER, ...OFFICE_UNTEN] : OFFICE_UNTEN)
              .filter((e) => !e.nurGemeinschaft || gemeinschaft)
              .map((e) => (
              <button
                key={e.id}
                id={`nav-${e.id}`}
                type="button"
                className={e.id === aktiv ? 'active' : undefined}
                aria-current={e.id === aktiv ? 'page' : undefined}
                onClick={() => waehle(e.id)}
              >
                <Zeichen name={e.icon} groesse={18} />
                <span>{e.label}</span>
              </button>
            ))}
          </div>
        </nav>

        {/*
          Der Name steht oben rechts in der Kopfleiste. Hier stand er ein
          zweites Mal – und ein Name, der zweimal auf demselben Bildschirm
          steht, sieht aus wie ein Fehler und nicht wie Betonung.
        */}
        <div className="office-side-foot">
          <div style={{ display: 'flex', gap: 2 }}>
            {aufStartseite && (
              <button
                className="o-btn o-btn-ghost"
                id="btn-uebersicht"
                onClick={aufStartseite}
                title="Öffentliche Übersicht"
              >
                Übersicht
              </button>
            )}
            <button
              className="o-btn o-btn-ghost"
              id="btn-abmelden"
              onClick={() => void aufAbmelden()}
            >
              Abmelden
            </button>
          </div>

          {/*
            Unsere Marke, ganz unten und klein.

            Sie ganz wegzulassen wäre der naheliegende nächste Schritt und
            wäre falsch: In diesem Markt empfehlen sich Betriebe gegenseitig,
            und der Bauführer, der die Software beim Kollegen auf dem Laptop
            sieht, fragt nur nach, wenn dort ein Name steht, den er sich
            merken kann. Klein genug, um nicht zu konkurrieren – gross genug,
            um gelesen zu werden.
          */}
          <div className="office-side-marke">
            <span>powered by</span>
            <Marke groesse={14} />
          </div>
        </div>
      </aside>

      <main className="office-main">
        {/*
          Die Kopfleiste.

          Sie gehört in den Rahmen und nicht in jeden einzelnen Bildschirm –
          sonst steht die Suche auf acht Seiten acht Mal leicht verschieden da.

          Das Suchfeld ist ein echtes Feld und kein Bild: es nimmt Eingaben
          entgegen und gibt sie nach oben weiter. Wo noch nicht gesucht werden
          kann, steht es gar nicht erst da – ein Feld, das nichts tut, ist
          schlimmer als keines.
        */}
        <div className="office-top">
          {/*
            Die Brotkrume: Betrieb, dann Bereich.

            Der Bereichsname stand bisher nur als grosse Überschrift im Inhalt
            – und die scrollt weg. Wer eine lange Rechnungsliste durchgeht und
            dann eine Frage beantwortet, sieht oben nichts mehr, das sagt, wo
            er ist. Hier bleibt es stehen, weil die Leiste klebt.

            Auf schmalen Bildschirmen verschwindet der Betriebsname zuerst:
            der ist überall gleich, der Bereich nicht.
          */}
          <div className="office-brotkrume">
            <span className="office-brotkrume-betrieb">{firma}</span>
            <span aria-hidden="true">/</span>
            <span className="office-brotkrume-ort">{bereichsname(aktiv)}</span>
          </div>

          {/*
            Der Weg in die Befehlszeile, für alle, die kein ⌘K kennen.

            Das Tastenkürzel steht sichtbar darauf. Ein Kürzel, das nirgends
            geschrieben steht, kennt nur, wer es schon kennt – und dann
            braucht es die Befehlszeile ohnehin nicht mehr.
          */}
          <button
            className="office-befehl"
            id="btn-befehlszeile"
            type="button"
            onClick={() => setBefehlOffen(true)}
          >
            <Zeichen name="suche" groesse={16} />
            <span>Suchen oder springen</span>
            <kbd>⌘K</kbd>
          </button>

          {aufSuche && (
            <form
              className="office-suche"
              role="search"
              onSubmit={(e) => {
                e.preventDefault()
                aufSuche(suchtext.trim())
              }}
            >
              <Zeichen name="suche" groesse={17} />
              <input
                autoComplete="off"
                id="office-suche"
                type="search"
                value={suchtext}
                placeholder="Suche …"
                aria-label="Suche"
                onChange={(e) => setSuchtext(e.target.value)}
              />
            </form>
          )}

          <button
            type="button"
            className={hinweise > 0 ? 'office-glocke hat-neues' : 'office-glocke'}
            id="btn-hinweise"
            aria-label={hinweise > 0 ? `${hinweise} offene Hinweise` : 'Keine Hinweise'}
            title={hinweise > 0 ? `${hinweise} offene Hinweise` : 'Keine Hinweise'}
            /*
              Zur Arbeitsliste, nicht zur Übersicht. Von der Übersicht aus war
              der Klick wirkungslos – man stand schon dort.
            */
            onClick={() => aufWechsel('offen')}
          >
            <Zeichen name="glocke" groesse={19} />
          </button>

          <div className="office-benutzer">
            <span>{benutzer}</span>
          </div>
        </div>

        {/*
          Das Ausstiegsband – über dem Inhalt und nicht in ihm.

          Es steht hier und nicht in den einzelnen Bildschirmen, weil es für
          alle gilt: Ein Hinweis, der nur auf der Übersicht erscheint, fehlt
          genau dann, wenn jemand den Tag über in den Rechnungen arbeitet.
          Gezeichnet wird es nur, wenn der Server einen Grund dafür liefert.
        */}
        <Ausstiegsband aufAbo={() => aufWechsel('meinraporto')} />

        <div className="office-page">{children}</div>
      </main>

      <Befehlszeile
        befehle={alleBefehle}
        offen={befehlOffen}
        aufSchliessen={() => setBefehlOffen(false)}
      />

      {/*
        Die kurzen Meldungen – und zwar hier, weil sie hier gefehlt haben.

        Der Meldungsort stand ausschliesslich im Monteur-Rahmen (`Shell` in
        `App.tsx`). Die beiden Rahmen schliessen sich aus: Wer als Büro
        angemeldet ist, bekommt `OfficeShell` und damit keinen Ort, an dem eine
        Meldung erscheinen könnte. Jeder `notify.ok` und jeder `notifyError`
        aus einem Bürobildschirm ging damit ins Leere – auch die Fehlermeldung
        einer Sammelaktion, auch die Bestätigung nach dem Abschliessen.

        Gemessen am gebauten Stand: nach einer erfolgreichen Sammelaktion war
        `.toasts` im Dokument nicht vorhanden; der Satz wurde erzeugt und
        nirgends gezeichnet.

        Ein Ort je Rahmen, nicht mehr: Zwei gezeichnete Meldungsorte hingen an
        derselben Ablage und zeigten jede Meldung doppelt.
      */}
      <Toasts />
    </div>
  )
}

/** Kopfzeile eines Bürobildschirms – Titel, Unterzeile, Aktionen rechts. */
export function OfficeHead({
  titel,
  unter,
  children
}: {
  titel: string
  unter?: string
  children?: ReactNode
}): JSX.Element {
  return (
    <div className="office-head">
      <div>
        <h1>{titel}</h1>
        {unter && <div className="sub">{unter}</div>}
      </div>
      {children && <div className="office-actions">{children}</div>}
    </div>
  )
}
